'use client';

import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { SupportUserSummary } from '../../services/generated/Api';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  useMarkSupportRead,
  useSendSupportFile,
  useSendSupportMessage,
  useSupportConversation,
} from '../../queries/useSupportQueries';
import { getPortalRoot } from '../portalRoot';
import { FiX } from '../icons';
import { cn } from '../../utils/cn';
import { Spinner } from '../ui/Spinner';
import { SupportComposer } from './SupportComposer';
import { SupportMessageList, type PendingMessage } from './SupportMessageList';
import translations from './SupportChat.translations';

interface Outgoing extends PendingMessage {
  file: File | null;
}

/**
 * On a phone the keyboard shrinks the visual viewport without moving the
 * layout viewport, so a plain `inset-0` panel puts the composer under the
 * keyboard. Track the visual viewport and size the panel to it.
 */
function useVisualViewport(): { height: number; top: number } | null {
  const [rect, setRect] = useState<{ height: number; top: number } | null>(null);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => setRect({ height: viewport.height, top: viewport.offsetTop });
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }, []);

  return rect;
}

function useIsPageVisible(): boolean {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const update = () => setIsVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  return isVisible;
}

interface SupportChatPanelProps {
  summary: SupportUserSummary | undefined;
  onClose: () => void;
}

export const SupportChatPanel: FC<SupportChatPanelProps> = ({ summary, onClose }) => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const viewport = useVisualViewport();
  const isPageVisible = useIsPageVisible();

  const conversation = useSupportConversation(true);
  const sendMessage = useSendSupportMessage();
  const sendFile = useSendSupportFile();
  const { mutate: markAsRead } = useMarkSupportRead();

  const [outgoing, setOutgoing] = useState<Outgoing[]>([]);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const outgoingCounter = useRef(0);
  const markedStampRef = useRef<string | null>(null);

  useEffect(() => {
    if (isDesktop) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isDesktop]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Seeing the newest messages is what counts as reading them — not
  // opening the panel, and not a poll landing while the tab is hidden.
  useEffect(() => {
    if (!isAtBottom || !isPageVisible) return;
    if (!summary || summary.unreadCount === 0) return;
    if (conversation.messages.length === 0) return;

    const stamp = summary.lastMessageAt ?? null;
    if (markedStampRef.current === stamp) return;
    markedStampRef.current = stamp;
    markAsRead();
  }, [isAtBottom, isPageVisible, summary, conversation.messages.length, markAsRead]);

  const drop = useCallback((localId: string) => {
    setOutgoing((items) => {
      const gone = items.find((item) => item.localId === localId);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return items.filter((item) => item.localId !== localId);
    });
  }, []);

  const deliver = useCallback(
    (item: Outgoing) => {
      const onError = () =>
        setOutgoing((items) =>
          items.map((current) =>
            current.localId === item.localId ? { ...current, hasFailed: true } : current,
          ),
        );

      if (item.file) {
        sendFile.mutate(
          { file: item.file, caption: item.text ?? undefined },
          { onSuccess: () => drop(item.localId), onError },
        );
        return;
      }

      sendMessage.mutate(item.text ?? '', {
        onSuccess: () => drop(item.localId),
        onError,
      });
    },
    [drop, sendFile, sendMessage],
  );

  const handleSend = useCallback(
    ({ text, file }: { text: string; file: File | null }) => {
      outgoingCounter.current += 1;
      const item: Outgoing = {
        localId: `outgoing-${outgoingCounter.current}`,
        text: text || null,
        fileName: file?.name ?? null,
        previewUrl: file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        hasFailed: false,
        file,
      };
      setOutgoing((items) => [...items, item]);
      setIsAtBottom(true);
      deliver(item);
    },
    [deliver],
  );

  const handleRetry = useCallback(
    (localId: string) => {
      setOutgoing((items) =>
        items.map((item) => (item.localId === localId ? { ...item, hasFailed: false } : item)),
      );
      const item = outgoing.find((current) => current.localId === localId);
      if (item) deliver({ ...item, hasFailed: false });
    },
    [deliver, outgoing],
  );

  const isEmpty =
    !conversation.isLoading &&
    !conversation.isError &&
    conversation.messages.length === 0 &&
    outgoing.length === 0;

  return createPortal(
    <div className={cn('fixed z-[1150]', isDesktop ? 'bottom-6 end-6' : 'start-0 end-0')}
      style={
        isDesktop
          ? undefined
          : { top: viewport?.top ?? 0, height: viewport?.height ?? undefined }
      }
    >
      <div
        className={cn(
          'flex flex-col overflow-hidden bg-bg-secondary',
          isDesktop
            ? 'h-[560px] max-h-[calc(100vh-96px)] w-[380px] rounded-3xl border border-separator-non-opaque shadow-[0_16px_48px_rgba(0,0,0,0.55)]'
            : 'h-full w-full',
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center gap-3 border-b border-separator-non-opaque px-4 pb-3',
            isDesktop ? 'pt-3' : 'pt-[max(12px,var(--tg-viewport-safe-area-inset-top,0px))]',
          )}
        >
          <div className="flex min-w-0 flex-col">
            <span className="text-[16px] font-semibold text-label-primary">{t('title')}</span>
            <span className="text-[12px] text-label-tertiary">{t('subtitle')}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-label-secondary active:opacity-70"
          >
            <FiX size={16} />
          </button>
        </div>

        {conversation.isLoading && conversation.messages.length === 0 ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner size="m" />
          </div>
        ) : conversation.isError && conversation.messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
            <span className="text-[14px] text-label-secondary">{t('loadError')}</span>
            <button
              type="button"
              onClick={() => conversation.refetch()}
              className="rounded-full bg-fill-tertiary px-4 py-2 text-[13px] text-label-primary active:opacity-70"
            >
              {t('retry')}
            </button>
          </div>
        ) : isEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
            <span className="text-[15px] font-semibold text-label-primary">{t('emptyTitle')}</span>
            {summary?.canWrite !== false && (
              <span className="text-[13px] text-label-tertiary">{t('emptyText')}</span>
            )}
          </div>
        ) : (
          <SupportMessageList
            messages={conversation.messages}
            pending={outgoing}
            canLoadOlder={conversation.canLoadOlder}
            isLoadingOlder={conversation.isLoadingOlder}
            onLoadOlder={conversation.loadOlder}
            onRetryPending={handleRetry}
            onAtBottomChange={setIsAtBottom}
          />
        )}

        <SupportComposer
          canWrite={summary?.canWrite ?? true}
          refusal={summary?.writeRefusal ?? null}
          onSend={handleSend}
        />
      </div>
    </div>,
    getPortalRoot(),
  );
};
